import { useEffect, useState } from 'react';
import { cur } from '@/lib/currency';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import api from '@/lib/api';

// iter952 — Variance drill-down. Lists the journal entries whose own lines
// don't balance (the ones pushing the ledger out of balance). Reuses the
// /accounting/trial-balance/variance endpoint (same _compute_trial_balance scope).
export function VarianceDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/accounting/trial-balance/variance')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open]);

  const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const entries = data?.unbalanced_entries || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="variance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={18} /> Ledger Variance
          </DialogTitle>
          <DialogDescription>
            Grand Total Debits − Credits ={' '}
            <span className="font-mono font-semibold text-red-700">{fmt(data?.difference)}</span>. These journal entries have unbalanced lines.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="py-8 text-center text-sm text-emerald-700 flex flex-col items-center gap-2">
            <CheckCircle2 size={28} className="text-emerald-600" />
            No unbalanced journal entries found. The variance may come from a rounding/suspense adjustment.
          </div>
        ) : (
          <div className="max-h-[55vh] overflow-auto border border-border rounded-lg" data-testid="variance-list">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Entry</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-right px-3 py-2 font-medium">Debit</th>
                  <th className="text-right px-3 py-2 font-medium">Credit</th>
                  <th className="text-right px-3 py-2 font-medium">Diff</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {entries.map((e) => (
                  <tr key={e.id} className="hover:bg-red-50/50" data-testid={`variance-row-${e.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs font-semibold text-foreground">{e.entry_number || e.id?.slice(0, 8)}</div>
                      <div className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={e.description}>{e.description || e.entry_type}</div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{e.date}</td>
                    <td className="px-3 py-2 text-right font-mono text-success">{fmt(e.total_debit)}</td>
                    <td className="px-3 py-2 text-right font-mono text-error">{fmt(e.total_credit)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-red-700">{fmt(e.difference)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default VarianceDialog;
